import * as d3 from 'd3';
import { chain, flatMap, max, maxBy, min, minBy, orderBy, sum, sumBy } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

export interface TimelineChartProps {
  width: number;
  height: number;
  dataSeries?: TimelineChartDataPoint[][];
  yTickCount?: number;
  xTickCount?: number;
  yLabelTooltip: string;
  fontSizeAxes?: number;
  fontSizeTooltip?: number;
  yMinPlaceholder?: number;
  enableTooltips?: boolean;
  yMaxPlaceholder?: number;
  showYGrid?: boolean;
  showXGrid?: boolean;
  showXLabels?: boolean;
  showYLabels?: boolean;
  strokeWidth?: number;
  reverseY?: boolean;
  stacked?: boolean;
  dataColors?: DataColor[];
  disableArea?: boolean;
  paddingLeft?: number;
  paddingRight?: number;
  paddingTop?: number;
  paddingBottom?: number;
  loadingPlaceholder?: boolean;
}

export interface TimelineChartDataPoint {
  date: Date;
  value: number;
}

export interface DataColor {
  fillClass: string;
  strokeClass: string;
  tooltipInnerCircleClass: string;
  tooltipOuterCircleClass: string;
}

export const TimelineChart: React.FC<TimelineChartProps> = React.memo(
  ({
    width,
    height,
    dataSeries,
    yTickCount = 4,
    xTickCount = 4,
    yLabelTooltip,
    fontSizeAxes = 18,
    fontSizeTooltip = 24,
    yMinPlaceholder = 0,
    yMaxPlaceholder = 100,
    enableTooltips = false,
    showXGrid = true,
    showYGrid = true,
    showXLabels = true,
    showYLabels = true,
    strokeWidth = 8,
    reverseY = false,
    stacked = false,
    dataColors = [
      {
        fillClass: 'fill-primary-200/50',
        strokeClass: 'stroke-primary-700',
        tooltipOuterCircleClass: 'fill-primary-700',
        tooltipInnerCircleClass: 'fill-primary-300',
      },
    ],
    disableArea = false,
    paddingBottom = 1,
    paddingTop = 1,
    paddingLeft = 1,
    paddingRight = 1,
    loadingPlaceholder,
  }) => {
    // Calculate scales and ticks
    const { xTicks, analyzedDataPoints, yTicks, xScale, yScale, maxYValue, minYValue } =
      useMemo(() => {
        const sampleSeries = dataSeries[0];
        // Reorganize datapoints by date so that it's { date: ..., values: [series1Point, series2Point,...]  }
        // This is beneficial for the various geometries in the chart
        const analyzedDataPoints = sampleSeries?.map((sampleDataPoint, dataPointIndex) => {
          const dateValues = dataSeries.map((series) => series[dataPointIndex]?.value); // One value per series in order
          return {
            date: sampleDataPoint.date,
            values: dateValues,
            valueStart: sampleDataPoint.value,
            valueMin: min(dateValues),
            valueMax: max(dateValues),
            valueAccumulated: sum(dateValues),
          };
        });

        // Y axis
        let minYValue = Math.min(
          ...analyzedDataPoints?.map((t): number => (stacked ? t.valueMin : min(t.values))),
          yMinPlaceholder
        );

        const maxYValue = Math.max(
          ...analyzedDataPoints?.map((t): number => (stacked ? t.valueAccumulated : max(t.values))),
          yMaxPlaceholder
        );

        if (minYValue === maxYValue) minYValue = 0;
        let yDomain = [maxYValue, minYValue];
        if (reverseY) yDomain = yDomain.reverse();
        let yScale = d3
          .scaleLinear()
          .domain(yDomain)
          .range([paddingTop, height - paddingBottom]);
        if (showYGrid || showYLabels) yScale = yScale.nice();
        const yTicks = yScale.ticks(yTickCount);

        // X axis
        const minDate = minBy(analyzedDataPoints, (tv) => moment(tv.date))?.date;
        const maxDate = maxBy(analyzedDataPoints, (tv) => moment(tv.date)?.unix())?.date;
        let xScale = d3
          .scaleTime()
          .domain([minDate, maxDate])
          .range([paddingLeft, width - paddingRight]);
        if (showYGrid || showXLabels) xScale = xScale.nice();
        const xTicks = xScale.ticks(xTickCount);

        return { xTicks, analyzedDataPoints, yTicks, xScale, yScale, minYValue, maxYValue };
      }, [
        dataSeries,
        yTickCount,
        paddingLeft,
        paddingBottom,
        paddingTop,
        paddingRight,
        height,
        width,
      ]);

    // Chart lines and areas
    const stackValues = dataSeries[0]?.map((x) => ({ date: x.date, value: 0 }));
    const svgPaths = dataSeries.map((dataPoints, seriesIndex) => {
      // Create the line
      const svgLine = dataPoints
        .map((dataPoint, index) => {
          // Set local stack value
          let stackValue = 0;
          if (stacked) {
            stackValue = stackValues[index].value;
          }

          // Create point coordinates
          return `${xScale(dataPoint.date)},${yScale(dataPoint.value + stackValue)} `;
        })
        .join('');

      // Create the bottom part of the area
      // ... it's made in reverse date order so that we can connect the lines correctly
      const svgAreaBelowSection = [...stackValues].reverse().map((stack, index) => {
        const startY = height - paddingBottom;
        return `${xScale(stack.date)},${seriesIndex === 0 ? startY : yScale(stack.value)} `;
      });

      // Create the above part of the area
      const svgAreaAboveSection = dataPoints
        .map((dataPoint, index) => {
          // Set local stack value
          let stackValue = 0;
          if (stacked) {
            stackValue = stackValues[index].value;
          }

          // Create point coordinates
          return `${xScale(dataPoint.date)},${yScale(stackValue + dataPoint.value)} `;
        })
        .join(' ');

      // Increase stack hight for next iteration
      if (stacked) {
        dataPoints.forEach((dataPoint, index) => {
          stackValues[index].value += dataPoint.value;
        });
      }
      const svgArea = `${svgAreaBelowSection} ${svgAreaAboveSection}`;

      return { svgLine, svgArea };
    });

    // X-axis formatting
    let xTickFormatting = 'DD MMM YY';
    const wholeMonths =
      xTicks.every((xTick) => xTick.getDate() === 1) &&
      xTicks[0]?.getMonth() !== xTicks[1]?.getMonth();
    if (wholeMonths) xTickFormatting = 'MMM YY';

    // Y-axis formatting
    let ySampleValue = yTicks[0];
    let ySampleIncrement = 0;
    if (yTicks.length > 1) {
      ySampleIncrement = yTicks[0] - yTicks[1];
    }
    if (ySampleValue === 0) {
      ySampleIncrement = maxYValue - minYValue;
    }
    if (ySampleIncrement === 0) {
      ySampleIncrement = ySampleValue * 0.1;
    }
    if (ySampleIncrement === 0) {
      ySampleIncrement = 1;
    }

    const yDecimalPrecision = d3.precisionPrefix(ySampleIncrement, ySampleValue);
    const yFormatter = d3.formatPrefix('.' + yDecimalPrecision, ySampleValue);

    // Mouse position
    const [svgRef, setSvgRef] = useState<SVGSVGElement>(null);
    const svgCursorPoint = useMemo(() => svgRef?.createSVGPoint(), [svgRef]);
    const [throttledPosition, setThrottledPosition] = useState<{
      x: number;
      y: number;
      inView: boolean;
      throttle: boolean;
    }>({ x: 0, y: 0, inView: false, throttle: false });

    useEffect(() => {
      if (!throttledPosition.throttle) return;
      setTimeout(() => setThrottledPosition((prev) => ({ ...prev, throttle: false })), 50);
    }, [throttledPosition.throttle]);

    // Tooltip
    const tooltipPaths = useMemo(() => {
      if (!enableTooltips || !throttledPosition.inView) return;
      const tooltipStrokeWidth = 2;
      const tooltipWidth = fontSizeTooltip * 14;
      const tooltipHeight = fontSizeTooltip * 5;
      const desiredX = throttledPosition.x;
      const desiredY = paddingTop + tooltipHeight / 2;
      const tooltipOrigin = {
        x: Math.max(
          tooltipStrokeWidth,
          Math.min(desiredX, width - tooltipWidth - tooltipStrokeWidth)
        ),
        y: Math.max(
          tooltipStrokeWidth,
          Math.min(desiredY, height - tooltipHeight - tooltipStrokeWidth)
        ),
      };

      // Calculate closest point
      const stackHeights = dataSeries[0]?.map((ds) => 0);
      const analyzed = chain(dataSeries)
        .flatMap((series, seriesIndex) =>
          series.map((dataPoint, dataPointIndex) => {
            const stack = stackHeights[dataPointIndex];
            if (stacked) {
              stackHeights[dataPointIndex] += dataPoint.value;
            }
            return {
              ...dataPoint,
              dataPointIndex,
              seriesIndex,
              stack,
              distance: Math.sqrt(
                Math.pow(throttledPosition.x - xScale(dataPoint.date), 2) +
                  Math.pow(throttledPosition.y - yScale(dataPoint.value + stack), 2)
              ),
            };
          })
        )
        .value();
      const closestPoint = minBy(analyzed, (x) => x.distance);

      if (!closestPoint) return;

      const closestPointX = xScale(closestPoint.date);
      const closestPointY = yScale(closestPoint.value + closestPoint.stack);

      // Setup corners
      const tooltipCorners = [
        { x: tooltipOrigin.x, y: tooltipOrigin.y },
        { x: tooltipOrigin.x + tooltipWidth, y: tooltipOrigin.y },
        { x: tooltipOrigin.x + tooltipWidth, y: tooltipOrigin.y + tooltipHeight },
        { x: tooltipOrigin.x, y: tooltipOrigin.y + tooltipHeight },
      ];

      // Setup the tooltip path
      const tooltipCenter = {
        x: tooltipOrigin.x + tooltipWidth / 2,
        y: tooltipOrigin.y + tooltipHeight / 2,
      };

      const triangleWidth = tooltipHeight / 7;
      const triangleHeight = tooltipHeight / 7;
      const tooltipSquare = `M ${tooltipCorners[0].x} ${tooltipCorners[0].y} L ${tooltipCorners[1].x} ${tooltipCorners[1].y} L ${tooltipCorners[2].x} ${tooltipCorners[2].y} L ${tooltipCorners[3].x} ${tooltipCorners[3].y} L ${tooltipCorners[0].x} ${tooltipCorners[0].y}`;
      const tooltipTriangle = `M ${tooltipCorners[0].x + tooltipStrokeWidth / 2} ${
        tooltipCenter.y + triangleHeight
      } L  ${tooltipCorners[0].x - triangleWidth} ${tooltipCenter.y} L ${
        tooltipCorners[0].x + tooltipStrokeWidth / 2
      } ${tooltipCenter.y - triangleHeight}`;
      const tooltipConnectionLine = `M ${closestPointX} ${closestPointY} L ${
        tooltipCorners[0].x - triangleWidth
      } ${tooltipCenter.y}`;

      return {
        tooltipSquare,
        tooltipTriangle,
        tooltipConnectionLine,
        closestPoint,
        closestPointX,
        closestPointY,
        tooltipOrigin,
        tooltipCorners,
        tooltipWidth,
        tooltipHeight,
        tooltipStrokeWidth,
      };
    }, [
      enableTooltips,
      throttledPosition,
      dataSeries,
      paddingLeft,
      paddingBottom,
      paddingTop,
      paddingRight,
      height,
      width,
    ]);

    if (loadingPlaceholder) {
      return <div className="rounded bg-gray-200/50 animate-pulse" style={{ height, width }}></div>;
    }

    return (
      <svg
        ref={setSvgRef}
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        onMouseMove={(event) => {
          if (!enableTooltips) return;
          if (throttledPosition.throttle) return;
          svgCursorPoint.x = event.clientX;
          svgCursorPoint.y = event.clientY;
          const transformedPoint = svgCursorPoint.matrixTransform(svgRef.getScreenCTM().inverse());
          setThrottledPosition({
            x: transformedPoint.x,
            y: transformedPoint.y,
            inView: true,
            throttle: true,
          });
        }}
        onMouseLeave={() => {
          if (!enableTooltips && !throttledPosition.inView) return;
          setThrottledPosition((prev) => ({ ...prev, inView: false }));
        }}
      >
        {/* Y axis */}
        {yTicks.map((yTick, index) => (
          <g key={index}>
            {/* Horizontal line */}
            {showYGrid && (
              <line
                x1={paddingLeft}
                x2={width - paddingRight}
                y1={yScale(yTick)}
                y2={yScale(yTick)}
                className="stroke-voxel-gray-200"
                strokeWidth={1}
              />
            )}
            {/* Y axis label */}
            {showYLabels && (
              <text
                x={0}
                y={yScale(yTick)}
                fontSize={fontSizeAxes}
                fontWeight="normal"
                textAnchor="start"
                alignmentBaseline="central"
                className="font-sans font-regular fill-voxel-gray-400"
              >
                {yFormatter(yTick)}
              </text>
            )}
          </g>
        ))}

        {/* X axis */}
        {xTicks?.map((currentValue, index) => {
          return (
            <React.Fragment key={index}>
              {showXGrid && (
                <line
                  x1={xScale(currentValue)}
                  x2={xScale(currentValue)}
                  y1={paddingTop}
                  y2={height - paddingBottom}
                  strokeWidth={1}
                  className="stroke-voxel-gray-200"
                />
              )}
              {showXLabels && (
                <text
                  textAnchor="middle"
                  fontSize={fontSizeAxes}
                  className="font-sans font-regular fill-voxel-gray-500"
                  x={xScale(currentValue)}
                  y={height - 2}
                >
                  {moment(currentValue.toDateString()).format(xTickFormatting)}
                </text>
              )}
            </React.Fragment>
          );
        })}

        {showYGrid && (
          <>
            <line
              x1={paddingLeft}
              x2={width - paddingRight}
              y1={height - paddingBottom}
              y2={height - paddingBottom}
              strokeWidth={1}
              className="stroke-voxel-gray-200"
            />
          </>
        )}

        {showXGrid && (
          <>
            <line
              x1={paddingLeft}
              x2={paddingLeft}
              y1={paddingTop}
              y2={height - paddingBottom}
              strokeWidth={1}
              className="stroke-voxel-gray-200"
            />
            <line
              x1={width - paddingRight}
              x2={width - paddingRight}
              y1={paddingTop}
              y2={height - paddingBottom}
              strokeWidth={1}
              className="stroke-voxel-gray-200"
            />
          </>
        )}

        {svgPaths.map((path, index) => (
          <React.Fragment key={index}>
            {/* Area */}
            {!disableArea && (
              <polygon
                points={path.svgArea}
                strokeWidth={16}
                stroke="none"
                className={dataColors[index].fillClass}
              />
            )}
          </React.Fragment>
        ))}

        {svgPaths.map((path, index) => (
          <React.Fragment key={index}>
            {/* Line */}
            <polyline
              points={path.svgLine}
              className={dataColors[index].strokeClass}
              fill="none"
              strokeWidth={strokeWidth}
              strokeLinejoin="round"
            />
          </React.Fragment>
        ))}

        {/* Hovered point*/}
        {enableTooltips && throttledPosition.inView && !!tooltipPaths && (
          <>
            <path
              d={tooltipPaths.tooltipConnectionLine}
              stroke="none"
              strokeWidth={tooltipPaths.tooltipStrokeWidth}
              className=" stroke-black/5 transition-all ease-in-out duration-100"
            />
            <circle
              cx={tooltipPaths.closestPointX}
              cy={tooltipPaths.closestPointY}
              r={strokeWidth * 3}
              className={`${
                dataColors[tooltipPaths.closestPoint.seriesIndex].tooltipOuterCircleClass
              }`}
            />
            <circle
              cx={tooltipPaths.closestPointX}
              cy={tooltipPaths.closestPointY}
              r={strokeWidth * 1.5}
              strokeWidth={(strokeWidth * 2) / 3}
              className={`${
                dataColors[tooltipPaths.closestPoint.seriesIndex].tooltipInnerCircleClass
              } stroke-white`}
            />

            <path
              d={tooltipPaths.tooltipSquare}
              stroke="none"
              strokeWidth={tooltipPaths.tooltipStrokeWidth}
              className="fill-white stroke-black/5 transition-all  ease-in-out  duration-100"
            />

            <path
              d={tooltipPaths.tooltipTriangle}
              stroke="none"
              strokeWidth={tooltipPaths.tooltipStrokeWidth}
              className=" fill-white stroke-black/5  transition-all  ease-in-out  duration-100"
            />

            <g
              transform={`translate(${tooltipPaths.tooltipOrigin.x} ${
                tooltipPaths.tooltipOrigin.y + tooltipPaths.tooltipHeight / 2
              })`}
              className="transition-all  ease-in-out  duration-100"
            >
              <text fontSize={fontSizeTooltip} className="font-sans">
                <tspan
                  x={fontSizeTooltip}
                  y={-fontSizeTooltip}
                  alignmentBaseline="middle"
                  className="fill-voxel-gray-400 font-medium"
                >
                  {moment(tooltipPaths.closestPoint.date).format('MMM DD, yyyy')}
                </tspan>

                <tspan
                  x={fontSizeTooltip}
                  y={+fontSizeTooltip}
                  alignmentBaseline="middle"
                  className="fill-voxel-gray-700 font-medium"
                >
                  {yLabelTooltip}
                </tspan>
                <tspan
                  x={tooltipPaths.tooltipWidth - fontSizeTooltip}
                  y={+fontSizeTooltip}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="fill-voxel-gray-700 font-bold"
                >
                  {yFormatter(tooltipPaths.closestPoint.value)}
                </tspan>
              </text>
            </g>
          </>
        )}
      </svg>
    );
  }
);

export default TimelineChart;
